import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { X } from 'lucide-react';

interface ClientInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (clientName: string, advisorName: string) => void;
}

export function ClientInfoModal({ isOpen, onClose, onConfirm }: ClientInfoModalProps) {
  const { translations: t } = useLanguage();
  const [clientName, setClientName] = useState('');
  const [advisorName, setAdvisorName] = useState('');
  const clientInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on client input when modal opens
  useEffect(() => {
    if (isOpen && clientInputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        clientInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (clientName.trim() && advisorName.trim()) {
      onConfirm(clientName.trim(), advisorName.trim());
      // Reset form
      setClientName('');
      setAdvisorName('');
    }
  };

  const handleClose = () => {
    setClientName('');
    setAdvisorName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#1a1b25] border border-[#27272a] rounded-lg shadow-2xl w-full max-w-md p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">
            {t.clientInfoModal?.title || 'Client Information'}
          </h2>
          <button
            onClick={handleClose}
            className="text-[#a1a1aa] hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Name */}
          <div className="space-y-2">
            <label htmlFor="clientName" className="block text-sm font-medium text-white">
              {t.clientInfoModal?.clientNameLabel || 'Client Full Name'}
            </label>
            <input
              id="clientName"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder={t.clientInfoModal?.clientNamePlaceholder || 'John Smith'}
              className="w-full px-4 py-2 bg-[#0d0d12] border border-[#27272a] rounded-md text-white placeholder:text-[#52525b] focus:outline-none focus:ring-2 focus:ring-[#90d977] focus:border-transparent transition-all"
              required
              ref={clientInputRef}
            />
          </div>

          {/* Advisor Name */}
          <div className="space-y-2">
            <label htmlFor="advisorName" className="block text-sm font-medium text-white">
              {t.clientInfoModal?.advisorNameLabel || 'Advisor Full Name'}
            </label>
            <input
              id="advisorName"
              type="text"
              value={advisorName}
              onChange={(e) => setAdvisorName(e.target.value)}
              placeholder={t.clientInfoModal?.advisorNamePlaceholder || 'Jane Doe'}
              className="w-full px-4 py-2 bg-[#0d0d12] border border-[#27272a] rounded-md text-white placeholder:text-[#52525b] focus:outline-none focus:ring-2 focus:ring-[#90d977] focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="flex-1 border-[#27272a] bg-transparent text-white hover:bg-[#27272a] hover:border-[#3f3f46]"
            >
              {t.clientInfoModal?.cancelButton || 'Cancel'}
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#90d977] hover:bg-[#a3e887] text-black"
            >
              {t.clientInfoModal?.confirmButton || 'Generate PDF'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}